// Libs
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSplitButton,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import useDebounce from '~/utils/hooks/useDebounce';
import { IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { ICustomer } from '~/utils/interface/customer';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import {
  AcceptFileEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
  ToastTypeEnum,
} from '~/utils/enum/common';
import { customToast, formatPhoneNumber, formatTitleCase } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { customerActions, selectIsRefreshCustomerList } from '~/thunks/customer/customerSlice';
import {
  deleteCustomer,
  exportCustomer,
  getCustomerList,
  importCustomer,
  sendToQuickBooks,
} from '~/thunks/customer/customerThunk';
import { StorageEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './Customer.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onSendToQuickBooks: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<ICustomer>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      minWidth: 200,
      render: ({ avatarImage, firstName, lastName, ...rest }: ICustomer) => {
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'email',
      label: t('table_head_title_email'),
      render: (record: ICustomer) => record?.email,
    },
    {
      id: 'phone',
      label: t('table_head_title_phone'),
      minWidth: 150,
      render: (record: ICustomer) => {
        return formatPhoneNumber(record?.phoneNumber);
      },
    },
    {
      id: 'address',
      label: t('table_head_title_address'),
      minWidth: 350,
      render: (record: ICustomer) => record?.billingAddress?.address || EMPTY_STRING,
    },
    {
      id: 'quickBookCustomerId',
      label: t('table_head_title_quick_books'),
      align: 'center',
      render: (record: ICustomer) => (
        <Box component={'div'} className={cx('quickBooks', { connected: record?.quickBookCustomerId })}></Box>
      ),
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: ICustomer) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            {...(!record?.quickBookCustomerId && {
              onSendToQuickBooks: () => onSendToQuickBooks(record.id),
            })}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Customer = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/customer']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;

  const importCustomerRef = useRef<HTMLInputElement | null>(null);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshCustomerList = useSelector(selectIsRefreshCustomerList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamBase>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<ICustomer[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [idsCustomer, setIdsCustomer] = useState<string[]>([]);
  const [totalItems, setTotalItems] = useState<string>(EMPTY_STRING);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetCustomerList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshCustomerList) return;

    handleGetCustomerList(paramObject);
    dispatch(customerActions.setRefreshCustomerList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshCustomerList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetCustomerList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getCustomerList(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination, totalItems } = resp.data;
        if (!responses) return;

        setCustomerList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleNavigateToFormCustomer = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createCustomer);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createCustomer);
        break;
    }
  };

  const handleApplyFilter = (values: IQueryParamBase | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamBase | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleRowTableClick = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.customers}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.customers}/${id}`);
        break;
    }
  };

  const handleViewCustomer = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.customers}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.customers}/${id}`);
        break;
    }
  };

  const handleEditCustomer = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.editCustomer(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.editCustomer(id));
        break;
    }
  };

  const handleDeleteCustomer = (id: string) => {
    setIdsCustomer([id]);
    setIsShowConfirmDelete(true);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  // const handleRowsSelect = (ids: string[]) => {
  //   // TODO: Handle logic row select

  //   setIdsCustomer(ids);
  // };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleImportCustomer = () => {
    importCustomerRef?.current?.click();
  };

  const handleImportCustomerChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileUpload = event?.target?.files?.[DEFAULT_NUMBER_ZERO];

    if (fileUpload) {
      if (!fileUpload.name.endsWith(AcceptFileEnum.CSV)) {
        customToast(ToastTypeEnum.WARNING, t('common_warning_csv_file_import', { ns: 'common/base' }));
        return;
      }

      const formData = new FormData();
      formData.append('file', fileUpload);

      setIsLoading(true);

      dispatch(importCustomer(formData))
        .unwrap()
        .then((res) => {
          dispatch(customerActions.setRefreshCustomerList(true));
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleExportCustomer = () => {
    setIsLoading(true);

    const { page, limit, ...restData } = paramObject;

    dispatch(exportCustomer(restData))
      .unwrap()
      .then((res) => {
        const file = res?.data;

        if (!file) return;

        window.location.href = file?.url;
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  // const handleDeleteMultiple = () => {
  //   // Handle Delete Multiple
  //   setIsShowUnderDevelopment(true);
  // };

  const handleSendToQuickBooks = (customerId: string) => {
    setIsLoading(true);

    dispatch(sendToQuickBooks(customerId))
      .unwrap()
      .then((res) => {
        dispatch(customerActions.setRefreshCustomerList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setIdsCustomer([]);
  };

  const handleConfirmDelete = () => {
    if (!idsCustomer || idsCustomer?.length === DEFAULT_NUMBER_ZERO) return;

    setIsLoading(true);

    dispatch(deleteCustomer({ deleteIds: idsCustomer }))
      .unwrap()
      .then(() => {
        dispatch(customerActions.setRefreshCustomerList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_customers', { ns: 'common/sidebar' })}>
    <Box component={'div'} id='customerPageAdmin' className={cx('container')}>
      <BaseToolbar
        title={t('tile_quantity_customer_page', { quantity: totalItems })}
        // textAction={t('button_add_customer')}
        // onAction={handleNavigateToFormCustomer}
        // onDelete={handleDeleteMultiple}
        // selectedCount={idsCustomer.length}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters value={filterParams} defaultValue={FILTER_DEFAULT_VALUES} onApply={handleApplyFilter}>
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.EMAIL}
                      checked={value?.fields?.includes(SearchByFilterEnum.EMAIL) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_email')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.PHONE}
                      checked={value?.fields?.includes(SearchByFilterEnum.PHONE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_phone')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.ADDRESS}
                      checked={value?.fields?.includes(SearchByFilterEnum.ADDRESS) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_address')}
                />
              </FormGroup>
            </Stack>
          )}
        </BaseFilters>

        <BaseSplitButton label={t('button_add_customer')} onClick={handleNavigateToFormCustomer}>
          <Button variant='text' fullWidth onClick={handleImportCustomer}>
            {t('button_import_customer')}
          </Button>

          <Button variant='text' fullWidth onClick={handleExportCustomer}>
            {t('button_export_customer')}
          </Button>
        </BaseSplitButton>

        <input
          type='file'
          className={cx('inputImport')}
          ref={importCustomerRef}
          accept={AcceptFileEnum.CSV}
          onChange={handleImportCustomerChange}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            t,
            handleViewCustomer,
            handleEditCustomer,
            handleSendToQuickBooks,
            handleDeleteCustomer
          )}
          dataSource={customerList || []}
          // dataSelectedIds={idsCustomer}
          onPageChange={handlePageChange}
          // onSelectChange={handleRowsSelect}
          onRowClick={handleRowTableClick}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('confirm_delete_customer_title')}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default Customer;
